import React, { useEffect, useState } from "react";
import organizationClient from "@apis/organizationClient";
import useScopedOrgType from "@/hooks/useScopedOrgType";
import { OrgScopeConst, useOrg } from "@/contexts/OrgContext";
import OrganizationType from "@/cleanup/utils/OrganizationType";
import PageHeader from "@/cleanup/containers/PageHeader/PageHeader";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import NotAvailableAtThisLevel, { NotAvailableReasonConst } from "./NotAvailableAtThisLevel";

interface NotOnPartnerWithoutDevicesProps {
  /** The page's header, the label of the menu entry that opens it. */
  title: LanguageKey;
  children: React.ReactNode;
}

/**
 * STIC-11419 R6: a partner account holds no devices unless it has
 * legacy_partner_account_active_devices; they are in its stock organisation.
 * Wraps the device list routes greyed for such a sliced partner. The own level
 * hides those entries, so only a sliced partner is checked; a failed fetch
 * renders the page.
 */
const NotOnPartnerWithoutDevices: React.FC<NotOnPartnerWithoutDevicesProps> = ({
  title,
  children,
}) => {
  const { orgScope, scopeOrgId } = useOrg();
  const scopedOrgType = useScopedOrgType();
  const slicedPartnerId =
    orgScope === OrgScopeConst.SLICED && scopedOrgType === OrganizationType.PARTNER
      ? scopeOrgId
      : null;
  // The partner id the answer is for, so a stale one never shows for another org
  const [withoutDevices, setWithoutDevices] = useState<{ orgId: number; value: boolean } | null>(
    null
  );

  useEffect(() => {
    if (slicedPartnerId == null) {
      return;
    }
    let cancelled = false;
    organizationClient.findOne({ id: slicedPartnerId }).then(
      ({ data }: { data?: { allFeatureKeys?: string[] } }) => {
        if (!cancelled) {
          setWithoutDevices({
            orgId: slicedPartnerId,
            value: !data?.allFeatureKeys?.includes(FeatureKey.legacy_partner_account_active_devices),
          });
        }
      },
      () => {
        if (!cancelled) {
          setWithoutDevices({ orgId: slicedPartnerId, value: false });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [slicedPartnerId]);

  if (scopedOrgType === null) {
    return null;
  }
  if (slicedPartnerId != null) {
    if (withoutDevices?.orgId !== slicedPartnerId) {
      return null;
    }
    if (withoutDevices.value) {
      return (
        <>
          <PageHeader title={translate(title)} tabTitle={translate(title)} />
          <NotAvailableAtThisLevel reason={NotAvailableReasonConst.NOT_AT_THIS_PARTNER} />
        </>
      );
    }
  }
  return <>{children}</>;
};

export default NotOnPartnerWithoutDevices;
